// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ConditionalTooltip } from '@/components/conditional-tooltip';
import { FormNumberTextField } from '@/components/form-number-text-field';
import { FormTextField } from '@/components/form-text-field';
import {
  ArrowForwardOutlined,
  CheckOutlined,
  Inventory2Outlined,
  SettingsOutlined,
} from '@/constants/hardfin-standard-icons';
import { ReceiveDialogFormData } from '@/shipment/components/receive-dialog';
import { shipmentLineAllowsSerials } from '@/shipment/utilities/shipment-serials';
import { formatNumber } from '@/utilities/number';
import { pluralize } from '@/utilities/pluralize';
import { Box, Chip, Link, Paper, Stack, Typography } from '@mui/material';
import { Control, FieldError, UseFormStateReturn } from 'react-hook-form';

interface Props {
  index: number;
  item: ReceiveDialogFormData['items'][number];
  watchedItem: ReceiveDialogFormData['items'][number] | undefined;
  control: Control<ReceiveDialogFormData>;
  formState: UseFormStateReturn<ReceiveDialogFormData>;
  isEditMode: boolean;
  onSerialDrawerOpen: (index: number | null) => void;
  serialDrawerOpen: number | null;
  onClearErrors: (index: number) => void;
  invalidSerials?: Set<string>;
}

export function ReceiveDialogExistingItem(props: Props) {
  const shouldRenderSerialFields = shipmentLineAllowsSerials(
    props.watchedItem?.item
  );

  const invalidSerialCount = props.invalidSerials?.size ?? 0;
  const itemError = props.formState.errors.items?.[props.index] as
    | { serials?: FieldError }
    | undefined;
  const hasFormError = !!itemError?.serials;
  const hasError = invalidSerialCount > 0 || hasFormError;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderColor: hasError ? 'error.main' : 'divider',
        borderWidth: hasError ? 2 : 1,
      }}
    >
      <Stack gap={2}>
        <Stack direction="row" gap={3}>
          {/* Left column - Item info (60%) */}
          <Stack gap={1} sx={{ width: '60%' }}>
            <Stack direction="row" alignItems="center" gap={1}>
              {props.item.item?.hasAssets ? (
                <Inventory2Outlined sx={{ fontSize: '1.25rem' }} />
              ) : (
                <SettingsOutlined sx={{ fontSize: '1.25rem' }} />
              )}
              <Typography variant="subtitle1">
                {props.item.item?.sku}
              </Typography>
              {shouldRenderSerialFields && (
                <Chip
                  label="serialized"
                  size="small"
                  sx={{
                    height: '20px',
                    fontSize: '0.75rem',
                  }}
                />
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {props.item.item?.name}
            </Typography>
          </Stack>
          {/* Right column - Expected/Received (40%) */}
          <Stack gap={1} sx={{ width: '40%' }}>
            <Stack direction="row" alignItems="center" gap={2}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ width: '70px' }}
              >
                Expected:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  textAlign: 'right',
                  flex: 1,
                  pr: 1.5,
                }}
              >
                {formatNumber(props.item.initialQuantity ?? 0)}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" gap={2}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ width: '70px' }}
              >
                Received
              </Typography>
              <ConditionalTooltip
                arrow
                tooltipText={
                  props.isEditMode
                    ? 'Received quantities cannot be modified after receipt confirmation'
                    : undefined
                }
                sx={{ flex: 1 }}
              >
                <Box
                  sx={{
                    width: '100%',
                    '& input': {
                      textAlign: 'right',
                    },
                  }}
                >
                  <FormNumberTextField
                    nullable
                    numeric
                    control={props.control}
                    name={`items.${props.index}.quantity`}
                    disabled={props.isEditMode}
                    onChange={() => props.onClearErrors(props.index)}
                  />
                </Box>
              </ConditionalTooltip>
            </Stack>
          </Stack>
        </Stack>
        <Stack direction="row" gap={3} alignItems="center">
          <Box sx={{ width: '60%' }}>
            <ConditionalTooltip
              arrow
              tooltipText={
                props.isEditMode
                  ? 'Reference ID cannot be modified after receipt confirmation'
                  : undefined
              }
              sx={{ width: '70%' }}
            >
              <Box sx={{ width: '100%' }}>
                <FormTextField
                  fullWidth
                  label="Reference ID (optional)"
                  placeholder="Ex: batch number"
                  control={props.control}
                  name={`items.${props.index}.externalReference`}
                  disabled={props.isEditMode}
                />
              </Box>
            </ConditionalTooltip>
          </Box>
          <Box sx={{ width: '40%' }}>
            {shouldRenderSerialFields &&
              (() => {
                const serialCount = props.watchedItem?.serials?.length || 0;
                const expectedQuantity = props.watchedItem?.quantity || 0;
                const localItemError = props.formState.errors.items?.[
                  props.index
                ] as { serials?: FieldError } | undefined;
                const hasError = !!localItemError?.serials;

                let text = '';
                let icon = null;
                let color = 'text.secondary';

                if (props.isEditMode) {
                  // In edit mode, show read-only serial count
                  if (serialCount === 0) {
                    text = 'No serials';
                    color = 'text.secondary';
                  } else {
                    text = `${pluralize(serialCount, {
                      singular: 'serial',
                      plural: 'serials',
                      includeCount: true,
                    })}`;
                    icon = <ArrowForwardOutlined fontSize="small" />;
                    color = 'primary.main';
                  }
                } else if (invalidSerialCount > 0) {
                  // Show invalid serials count
                  text = `${invalidSerialCount} out of ${serialCount} ${pluralize(
                    serialCount,
                    {
                      singular: 'serial',
                      plural: 'serials',
                      includeCount: false,
                    }
                  )} ${invalidSerialCount === 1 ? 'is' : 'are'} invalid`;
                  color = 'error.main';
                } else if (serialCount === 0 && expectedQuantity === 0) {
                  text = 'Complete';
                  icon = <CheckOutlined fontSize="small" />;
                  color = 'success.main';
                } else if (serialCount === 0) {
                  text = 'No serials entered';
                  icon = <ArrowForwardOutlined fontSize="small" />;
                  color = hasError ? 'error.main' : 'text.secondary';
                } else if (serialCount === expectedQuantity) {
                  text = `${pluralize(serialCount, {
                    singular: 'serial',
                    plural: 'serials',
                    includeCount: true,
                  })} entered`;
                  icon = <CheckOutlined fontSize="small" />;
                  color = 'success.main';
                } else if (serialCount > expectedQuantity) {
                  const extra = serialCount - expectedQuantity;
                  text = `${pluralize(serialCount, {
                    singular: 'serial',
                    plural: 'serials',
                    includeCount: true,
                  })} entered (${extra} extra)`;
                  color = 'error.main';
                } else {
                  const needed = expectedQuantity - serialCount;
                  text = `${pluralize(serialCount, {
                    singular: 'serial',
                    plural: 'serials',
                    includeCount: true,
                  })} entered (${needed} needed)`;
                  color = hasError ? 'error.main' : 'warning.dark';
                }

                // In edit mode with no serials, just show text without link
                if (props.isEditMode && serialCount === 0) {
                  return (
                    <Typography
                      variant="body2"
                      sx={{
                        textAlign: 'right',
                        color: color,
                      }}
                    >
                      {text}
                    </Typography>
                  );
                }

                return (
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() =>
                      props.onSerialDrawerOpen(
                        props.serialDrawerOpen === props.index
                          ? null
                          : props.index
                      )
                    }
                    sx={{
                      textAlign: 'right',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: 0.5,
                      width: '100%',
                      cursor: 'pointer',
                      color: color,
                    }}
                  >
                    {text}
                    {icon}
                  </Link>
                );
              })()}
          </Box>
        </Stack>
      </Stack>
    </Paper>
  );
}
